import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { pollJobUntilDone } from "../lib/pollJob";
import { ZohoPushStatusBadge } from "../components/ZohoPushStatusBadge";
import StockItemSearch from "../components/StockItemSearch";
import AddressBlock from "../components/AddressBlock";
import {
  Plus,
  Truck,
  Eye,
  Loader2,
  Trash2,
  Search,
  X,
  RefreshCw,
  FileText,
  Pencil,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import BookVehicleDialog, { BookVehicleButton } from "../components/BookVehicleDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import useAuthStore from "@/store/authStore";
import PermButton from "@/components/PermButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Feb 2026 — defensive coerce so a legacy dispatch row with a
// timestamped `date` (e.g. "2026-07-27T00:00:00") shows the actual
// stored date in the Edit dialog's <input type="date"> instead of
// silently blanking to today. Display-layer only — never writes back.
const toYmd = (v) => (typeof v === "string" && v.length >= 10 && v[4] === "-" && v[7] === "-") ? v.slice(0, 10) : "";

const STATUS_COLORS = {
  PLAYFIELD_DRAFT: "bg-slate-100 text-slate-800 border-slate-300",
  DRAFT: "bg-amber-100 text-amber-900 border-amber-300",
  DRAFT_UNSYNCED: "bg-orange-100 text-orange-900 border-orange-300",
  APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-300",
  REJECTED: "bg-rose-100 text-rose-900 border-rose-300",
  PUSH_PENDING: "bg-sky-100 text-sky-900 border-sky-300",
  PUSH_FAILED: "bg-rose-100 text-rose-900 border-rose-300",
};

const STATUS_LABELS = {
  PLAYFIELD_DRAFT: "Playfield Draft",
  DRAFT: "Zoho Draft",
  DRAFT_UNSYNCED: "Zoho Draft — Not Synced",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  PUSH_PENDING: "Pushing to Zoho",
  PUSH_FAILED: "Push Failed",
};

// Tiny visual badge under the Rate input telling the user where the
// prefilled rate came from. Helps them know whether they're seeing a
// customer-specific saved rate vs. a list-price fallback vs. nothing.
const RATE_SOURCE_META = {
  customer_saleprice: { label: "Customer rate", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rm_prices_history:  { label: "From purchase history", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  price_master:        { label: "From price master", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

const RateSourceBadge = ({ source }) => {
  if (!source) return (
    <div className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200" data-testid="rate-source-none">
      No prior price
    </div>
  );
  const meta = RATE_SOURCE_META[source];
  if (!meta) return null;
  return (
    <div className={`mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded border ${meta.cls}`} data-testid={`rate-source-${source}`}>
      {meta.label}
    </div>
  );
};

const Dispatch = () => {
  const { token } = useAuthStore();
  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  // List state
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [branches, setBranches] = useState([]);
  // Customer dropdown list — reuses the same `/api/buyers` source as the
  // Dispatch Lots page (Feb 2026, P0). Value in the Select is the buyer's
  // display name; the backend filters `dispatch_invoices.customer_name`
  // (case-insensitive exact) since the invoice row stores a Zoho contact_id
  // that has no relation to `buyers.customer_code` / `buyers.id`.
  const [customers, setCustomers] = useState([]);

  // Filters + pagination
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterSourceBranch, setFilterSourceBranch] = useState("ALL");
  const [filterCustomer, setFilterCustomer] = useState("ALL");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);
  // Edit mode: when set, the create dialog acts as "Edit Invoice" and PUTs
  // to /dispatch-invoices/{id}. Behaviour depends on `editingStatus`:
  //   - PLAYFIELD_DRAFT: full edit (lines/qty/source/customer), submit
  //     buttons are "Save Draft" + "Push to Zoho"
  //   - DRAFT: header + per-line rate/desc/tax only (quantities locked),
  //     single "Save & Sync to Zoho" button
  const [editingId, setEditingId] = useState(null);
  const [bookVehicleOpen, setBookVehicleOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);

  // Feb 2026 — deep-link support. `Dispatch Lots → Submitted Dispatch
  // Data viewer → Continue to Dispatch Invoice` navigates here with
  // `?open=<dispatch_invoice_id>` so Finance lands directly on the
  // linked invoice's edit dialog (or the read-only detail dialog when
  // the invoice is past the editable statuses). The URL param is
  // cleared once handled so a page refresh doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openInvoiceParam = searchParams.get("open");

  // Create form
  const [form, setForm] = useState({
    source_branch: "",
    item_type: "RM",
    customer_id: "",
    customer_name: "",
    reference_number: "",
    notes: "",
    place_of_supply: "",
    date: new Date().toISOString().split("T")[0],
    // Invoice-level discount (May 2026). Applied BEFORE tax — Zoho
    // computes GST on (sub_total − discount).
    discount: "",
    discount_type: "percentage", // "percentage" | "amount"
    // Feb 2026 — dispatch_header convergence (AC1/AC2). Same field
    // shape as the Send-to-Finance dialog. `bill_to` / `ship_to` are
    // captured verbatim; `transporter.vendor_id` mirrors the S2F
    // TransporterInput; `vehicle_number` is a plain string. When
    // Dispatch.js picks a Zoho customer, we fetch
    // `/api/dispatch-invoices/buyer-addresses?customer_zoho_id=...`
    // and auto-populate with the default entry (falls back to blank
    // when no local buyer master is linked — see AC1 "when one exists").
    bill_to: null,
    ship_to: null,
    transporter_vendor_id: "",
    vehicle_number: "",
  });
  // Header prefill state — populated on customer pick.
  const [headerBillToOptions, setHeaderBillToOptions] = useState([]);
  const [headerShipToOptions, setHeaderShipToOptions] = useState([]);
  const [headerBuyerMatched, setHeaderBuyerMatched] = useState(false);
  // Feb 2026 (dispatch invoice address-sync fix) — display-only fields
  // resolved from `/buyer-addresses` alongside `matched`. When a match
  // exists we show them as a read-only strip under the customer search
  // ("Playfield Buyer: <name> · <code>") AND submit `buyer_id` in the
  // create payload so the backend doesn't repeat the reverse lookup.
  const [headerBuyerName, setHeaderBuyerName] = useState("");
  const [headerBuyerCode, setHeaderBuyerCode] = useState("");
  const [headerBuyerId, setHeaderBuyerId] = useState("");
  const [transporters, setTransporters] = useState([]);
  // Feb 2026 (AC7) — freight lines are now flattened into the same
  // `rows` state as SKU line items on Edit-open, marked with
  // `_is_freight=true`, and partitioned back into
  // `dispatch_header.freight_lines` at submit time. The prior separate
  // `freightLines` state hook was removed.
  const [itemOptions, setItemOptions] = useState([]);
  const [itemOptionsLoading, setItemOptionsLoading] = useState(false);
  const [zohoTaxes, setZohoTaxes] = useState([]);
  // Feb 2026 — Every line-item row carries a stable client-side `_key`
  // used as the React reconciliation key. Do NOT use the array index as
  // the key: removing a middle/first row from a list of 3+ makes React
  // reuse the top DOM nodes for the surviving rows and unmount the LAST
  // slot — visually the wrong row appears deleted while state is correct
  // (the classic "click row 1, row 3 disappears" symptom users reported).
  // Regenerated only on row creation; never mutated on state updates.
  const _newRowKey = () => (
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `k${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );
  const _blankRow = () => ({
    _key: _newRowKey(),
    item_id: "", quantity: "", rate: "", tax_id: "",
    item_name: "", item_unit: null, item_stock: null, rate_source: null,
  });
  const [rows, setRows] = useState([_blankRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [blockers, setBlockers] = useState(null);

  // Customer search
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custSearching, setCustSearching] = useState(false);

  // Source-lot picker (Feb 2026) — only relevant on CREATE + BUYER_SKU.
  // When `selectedLot` is set the customer + line_items are AUTO-FILLED
  // and LOCKED (no edits allowed per finance/logistics handoff rules).
  // The backend re-validates on save: any mismatch raises LOT_LINES_MISMATCH
  // or LOT_CUSTOMER_MISMATCH and the dispatch is refused.
  const [lotQuery, setLotQuery] = useState("");
  const [lotOptions, setLotOptions] = useState([]);
  const [lotLoading, setLotLoading] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);

  // ---------- Data loaders ----------
  const fetchDispatches = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        status: filterStatus,
        source_branch: filterSourceBranch,
      });
      if (filterCustomer && filterCustomer !== "ALL") {
        params.set("customer_name", filterCustomer);
      }
      if (filterDateFrom) params.set("date_from", filterDateFrom);
      if (filterDateTo) params.set("date_to", filterDateTo);
      const res = await axios.get(`${API}/dispatch-invoices?${params}`, {
        headers: getHeaders(),
      });
      setDispatches(res.data.dispatches || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dispatches");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches/names`, { headers: getHeaders() });
      setBranches(res.data.branches || []);
    } catch (err) {
      console.error("Failed to load branches", err);
    }
  };

  // Fetch buyer list for the Customer filter dropdown. Same endpoint the
  // Dispatch Lots page uses so both filters stay in sync.
  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/buyers`, { headers: getHeaders() });
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load buyers", err);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchCustomers();
  }, []);

  // Load Zoho taxes once for the create-dispatch tax dropdown
  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const res = await axios.get(`${API}/zoho/taxes`, { headers: getHeaders() });
        // Backend returns { taxes: [...], configured: bool } — normalize to array
        const list = Array.isArray(res.data) ? res.data : (res.data?.taxes || []);
        setZohoTaxes(list);
      } catch (err) {
        console.error("Failed to load Zoho taxes", err);
        setZohoTaxes([]);
      }
    };
    fetchTaxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDispatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterStatus, filterSourceBranch, filterCustomer, filterDateFrom, filterDateTo]);

  // Fetch item options when source branch, item_type, or selected customer
  // changes. Passing customer_id (Zoho contact_id) makes the backend pick
  // rates from `rm_saleprice` / `bsku_saleprice` first (customer-scoped
  // last sale price), falling back to list price.
  useEffect(() => {
    const fetchItems = async () => {
      if (!form.source_branch || !showCreate) {
        setItemOptions([]);
        return;
      }
      setItemOptionsLoading(true);
      try {
        const params = new URLSearchParams({
          source_branch: form.source_branch,
          item_type: form.item_type,
        });
        if (form.customer_id) params.set("customer_id", form.customer_id);
        const res = await axios.get(
          `${API}/dispatch-invoices/item-options?${params}`,
          { headers: getHeaders() }
        );
        setItemOptions(res.data.items || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load available stock");
        setItemOptions([]);
      } finally {
        setItemOptionsLoading(false);
      }
    };
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.source_branch, form.item_type, form.customer_id, showCreate]);

  // Debounced customer search
  useEffect(() => {
    if (!showCreate) return;
    const q = custQuery.trim();
    if (q.length < 2) {
      setCustResults([]);
      return;
    }
    let cancelled = false;
    setCustSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/zoho/customers/search?q=${encodeURIComponent(q)}`,
          { headers: getHeaders() }
        );
        if (!cancelled) setCustResults(res.data || []);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          toast.error("Failed to search Zoho customers");
          setCustResults([]);
        }
      } finally {
        if (!cancelled) setCustSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custQuery, showCreate]);

  // Feb 2026 — Transporters for the dispatch_header vendor picker. Fetched
  // once when the dialog opens (reuses `/api/vendors?vendor_type=Transporter`,
  // same source Send-to-Finance uses).
  useEffect(() => {
    if (!showCreate) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/vendors?vendor_type=Transporter&limit=500`,
          { headers: getHeaders() },
        );
        if (!cancelled) setTransporters(Array.isArray(res.data) ? res.data : (res.data?.items || []));
      } catch {
        if (!cancelled) setTransporters([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate]);

  // Feb 2026 — If we opened the dialog in EDIT mode against a lot- or
  // direct-created dispatch that already has a dispatch_header + linked
  // buyer, reload the buyer's address options so the selectors work.
  useEffect(() => {
    if (!showCreate || !form.customer_id || headerBillToOptions.length || headerShipToOptions.length) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/dispatch-invoices/buyer-addresses?customer_zoho_id=${encodeURIComponent(form.customer_id)}`,
          { headers: getHeaders() },
        );
        if (cancelled) return;
        setHeaderBillToOptions(res.data?.buyer_addresses?.bill_to || []);
        setHeaderShipToOptions(res.data?.buyer_addresses?.ship_to || []);
        setHeaderBuyerMatched(res.data?.buyer?.matched || false);
        setHeaderBuyerName(res.data?.buyer?.name || "");
        setHeaderBuyerCode(res.data?.buyer?.code || "");
        setHeaderBuyerId(res.data?.buyer?.id || "");
      } catch { /* no-op */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate, form.customer_id]);

  // Lot picker — async debounced search against PENDING_FINANCE lots.
  // Active on every CREATE dialog (regardless of item_type) so users can
  // discover the workflow even from the RM default. Lot selection
  // auto-flips item_type to BUYER_SKU before populating rows.
  useEffect(() => {
    if (!showCreate || editingId) {
      setLotOptions([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLotLoading(true);
      try {
        const params = new URLSearchParams({ limit: "50" });
        if (lotQuery) params.set("q", lotQuery);
        if (form.customer_id) params.set("customer_id", form.customer_id);
        const res = await axios.get(
          `${API}/dispatch-invoices/lot-options?${params}`,
          { headers: getHeaders() }
        );
        if (!cancelled) setLotOptions(res.data?.options || []);
      } catch (err) {
        if (!cancelled) {
          console.error("lot-options failed", err);
          toast.error("Failed to load dispatch lots");
        }
      } finally {
        if (!cancelled) setLotLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotQuery, form.customer_id, showCreate, editingId]);

  // ---------- Create helpers ----------
  const resetCreate = () => {
    setForm({
      source_branch: "",
      item_type: "RM",
      customer_id: "",
      customer_name: "",
      reference_number: "",
      notes: "",
      place_of_supply: "",
      date: new Date().toISOString().split("T")[0],
      discount: "",
      discount_type: "percentage",
      bill_to: null,
      ship_to: null,
      transporter_vendor_id: "",
      vehicle_number: "",
    });
    setRows([_blankRow()]);
    setCustQuery("");
    setCustResults([]);
    setBlockers(null);
    setHeaderBillToOptions([]);
    setHeaderShipToOptions([]);
    setHeaderBuyerMatched(false);
    setHeaderBuyerName("");
    setHeaderBuyerCode("");
    setHeaderBuyerId("");
    // Reset the lot-picker state too — preserves the "fresh dialog" feel.
    setLotQuery("");
    setLotOptions([]);
    setSelectedLot(null);
  };

  const openCreate = () => {
    resetCreate();
    setEditingId(null);
    setEditingStatus(null);
    setShowCreate(true);
  };

  // Open the same dialog in EDIT mode. Pre-fills every form field + line
  // items from the existing dispatch row. Submit branches on status:
  //   - PLAYFIELD_DRAFT: PUT can change everything; offers "Save Draft" and
  //     "Save & Push to Zoho" (atomic via `also_push_to_zoho`).
  //   - DRAFT (already in Zoho): PUT can only update header + rate/desc/tax;
  //     submit always syncs to Zoho.
  const openEdit = (d) => {
    setEditingId(d.id);
    setEditingStatus(d.status || null);
    const hdr = d.dispatch_header || null;
    setForm({
      source_branch: d.source_branch || "",
      item_type: d.item_type || "RM",
      customer_id: d.customer_zoho_id || "",
      customer_name: d.customer_name || "",
      reference_number: d.reference_number || "",
      notes: d.notes || "",
      place_of_supply: d.place_of_supply || "",
      date: toYmd(d.date) || new Date().toISOString().split("T")[0],
      discount: d.discount ? String(d.discount) : "",
      discount_type: d.discount_type || "percentage",
      // Hydrate the header fields from the persisted dispatch_header —
      // works verbatim for both direct-created and lot-created rows
      // (AC2: field shape is identical). Legacy pre-header rows
      // (AC4) hydrate to null/"" so the panel shows the blank
      // manual-entry state.
      bill_to: hdr?.bill_to && Object.keys(hdr.bill_to).length ? hdr.bill_to : null,
      ship_to: hdr?.ship_to && Object.keys(hdr.ship_to).length ? hdr.ship_to : null,
      transporter_vendor_id: hdr?.transporter?.vendor_id || "",
      vehicle_number: hdr?.vehicle_number || "",
    });
    // Feb 2026 (AC7) — freight_lines from Send-to-Finance are now
    // flattened into the same line-items list rendered inline with
    // SKUs. The separate "Freight Lines" section was removed. Split
    // back at submit time via the `_is_freight` marker on each row.
    const _freightRows = Array.isArray(hdr?.freight_lines)
      ? hdr.freight_lines.map((f) => ({
          _key: _newRowKey(),
          item_id: "",
          quantity: "1",
          rate: String(f.amount ?? ""),
          tax_id: f.tax_id || "",
          item_name: f.description || "Freight Outwards",
          item_unit: null,
          item_stock: null,
          is_freight: true,
          is_transportation_cost: false,
          hsn_or_sac: f.hsn_or_sac || "",
          _is_freight: true,
          _existing: true,
        }))
      : [];
    setRows([
      ...(d.line_items || []).map((li) => ({
      _key: _newRowKey(),
      item_id: li.item_id || "",
      quantity: String(li.quantity ?? ""),
      rate: String(li.rate ?? ""),
      tax_id: li.tax_id || "",
      // Feb 2026 — tax gap surface. Populated by the backend when the
      // draft-time resolver could not map (rate, kind) to a Zoho tax_id.
      // Renders an amber warning under the Tax select so the operator
      // can fix the SKU gst_rate / Zoho tax setup BEFORE pushing.
      gst_resolve_gap: li.gst_resolve_gap || null,
      gst_rate_stamped: li.gst_rate_stamped ?? null,
      gst_kind_stamped: li.gst_kind_stamped || null,
      item_name: li.name || "",
      item_unit: li.unit || null,
      item_stock: null,
      is_freight: (li.item_id || "").toUpperCase() === "FREIGHT",
      is_transportation_cost: (li.item_id || "").toUpperCase() === "TRANSPORTATION_COST",
      // Mark the row as "originally on this draft" so the inline stock-warn
      // check skips it. Net availability is reconciled by the backend at
      // save time (it restores the old debit, then validates the new debit
      // against post-restore inventory). The inline check was firing false
      // positives when the original draft had already debited stock — e.g.
      // a draft with qty=100 against 100 units on hand left current_stock=0,
      // making it impossible to even REDUCE the qty.
      original_qty: Number(li.quantity ?? 0),
    })), ..._freightRows]);
    setCustQuery("");
    setCustResults([]);
    setBlockers(null);
    setShowCreate(true);
  };

  // Feb 2026 — handle `?open=<dispatch_invoice_id>` deep-link fired
  // from `LotSubmittedViewerDialog` (Dispatch Lots page). Fetches the
  // full invoice row from the same detail endpoint the pencil-icon
  // uses, then routes it: PLAYFIELD_DRAFT / PUSH_FAILED / (DRAFT +
  // zoho_invoice_id) get `openEdit` — the same three states the
  // existing Edit button gates on; anything else opens the read-only
  // detail dialog. The `open` param is stripped from the URL after
  // handling so refreshing the page doesn't re-fire it, and after a
  // successful `openEdit` the dialog leaves an audit-friendly path
  // (`/dispatch`) in the address bar. Deliberately does NOT depend on
  // `dispatches` list — the linked invoice might not be on the
  // currently-loaded page.
  useEffect(() => {
    if (!openInvoiceParam) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/dispatch-invoices/${encodeURIComponent(openInvoiceParam)}`,
          { headers: getHeaders() }
        );
        if (cancelled) return;
        const row = res.data;
        const editable =
          row.status === "PLAYFIELD_DRAFT" ||
          row.status === "PUSH_FAILED" ||
          (row.status === "DRAFT" && row.zoho_invoice_id);
        if (editable) {
          openEdit(row);
        } else {
          setSelected(row);
          setShowDetail(true);
        }
      } catch (err) {
        if (cancelled) return;
        toast.error(
          err?.response?.status === 404
            ? "Linked dispatch invoice not found"
            : "Failed to open linked dispatch invoice"
        );
      } finally {
        if (!cancelled) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("open");
            return next;
          }, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openInvoiceParam]);

  const setRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((rs) => [...rs, _blankRow()]);
  const removeRow = (idx) =>
    setRows((rs) => (rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs));

  const pickItem = async (idx, item_id, info) => {
    // Freight & Transportation Cost are synthetic service lines — same
    // shape (qty locked at 1, no stock check, no master price lookup),
    // different Zoho catalog item + different GL account (Freight →
    // Freight Outwards, Transportation Cost → Shipping Charges (Income)).
    // The rate field carries the charge amount in both cases.
    if (item_id === "FREIGHT") {
      setRow(idx, {
        item_id: "FREIGHT",
        item_name: "Freight (service charge)",
        item_unit: null,
        item_stock: null,
        quantity: "1",
        rate: "",
        tax_id: "",
        rate_source: null,
        is_freight: true,
        is_transportation_cost: false,
        original_qty: undefined,
      });
      return;
    }
    if (item_id === "TRANSPORTATION_COST") {
      setRow(idx, {
        item_id: "TRANSPORTATION_COST",
        item_name: "Transportation Cost (service charge)",
        item_unit: null,
        item_stock: null,
        quantity: "1",
        rate: "",
        tax_id: "",
        rate_source: null,
        is_freight: false,
        is_transportation_cost: true,
        original_qty: undefined,
      });
      return;
    }
    // info comes either from itemOptions (full prefetch) or from StockItemSearch result
    let opt = itemOptions.find((o) => o.item_id === item_id) || info || null;

    // The StockItemSearch result may be a stripped match that doesn't carry
    // `prefilled_rate` / `rate_source` / `hsn_code` — common when the user
    // picks an SKU that has zero FG inventory at the source branch and
    // therefore never entered the item-options enrichment pipeline. Hit the
    // server-side resolver to fill in name + price (same cascade as the
    // dropdown: customer_saleprice → list price → null) before we set the
    // row. Without this, the row renders as bare SKU id with "No prior
    // price", which is the prod bug we're fixing.
    const needsServerResolve =
      !opt ||
      opt.prefilled_rate === undefined ||
      opt.rate_source === undefined;
    if (needsServerResolve && form.source_branch && form.item_type) {
      try {
        const params = new URLSearchParams({
          source_branch: form.source_branch,
          item_type: form.item_type,
          item_id,
        });
        if (form.customer_id) params.set("customer_id", form.customer_id);
        const r = await axios.get(
          `${API}/dispatch-invoices/item-resolve?${params.toString()}`,
        );
        if (r.data && r.data.found) {
          // Server-resolved values supersede the lightweight search result
          // for every enrichment field (name / unit / hsn / rate / source).
          opt = { ...(opt || {}), ...r.data };
        }
      } catch {
        // Falls through to the original info-only path — no regression.
      }
    }

    const rateRaw = opt?.prefilled_rate;
    const taxRaw = opt?.prefilled_tax_id;
    const source = opt?.rate_source || null;
    setRow(idx, {
      item_id,
      item_name: opt?.name || info?.name || "",
      item_unit: opt?.unit || null,
      item_stock: opt?.current_stock != null ? opt.current_stock : info?.current_stock,
      rate: rateRaw != null ? String(rateRaw) : "",
      tax_id: taxRaw || "",
      rate_source: source,
      // Oct 2026 (AC1) — the row carries the backend's structured
      // "why is this blank" verdict so the submit guard can block
      // with a clear named error (and the inline notice can distinguish
      // "fix the buyer link" from "add a Price Master row"), rather
      // than a silent soft badge next to a blank field.
      price_master_missing: !!opt?.price_master_missing,
      buyer_link_missing: !!opt?.buyer_link_missing,
      is_freight: false,
      is_transportation_cost: false,
      // A user-driven item change converts the row to a new line — inline
      // stock-warn must apply against current branch inventory.
      original_qty: undefined,
    });
    // Oct 2026 (AC1 fix) — the rate field for BUYER_SKU is readOnly on
    // this form (L2142), so the pre-fix "enter rate manually" toast was
    // actively misleading. Surface the actual gap and the fix. RM rows
    // stay editable, so their "type a rate" prompt is still correct.
    if (rateRaw == null) {
      if (form.item_type === "BUYER_SKU") {
        if (opt?.buyer_link_missing) {
          toast.error(
            `${item_id}: this Zoho customer isn't linked to a Playfield buyer — Price Master can't be resolved until the buyer link is set.`,
            { duration: 7000 }
          );
        } else {
          toast.error(
            `${item_id}: no active Price Master entry for this customer × SKU. Add one under Price Master, then re-pick the SKU.`,
            { duration: 7000 }
          );
        }
      } else {
        toast.warning(`No price on record for ${item_id} (rm_prices_history) — enter rate manually.`);
      }
    }
  };

  // submitMode is retained purely as a legacy signature — the ONLY
  // supported creation flow is now `playfield_draft` (Feb 2026). The
  // parameter is kept so callers passing it don't break; any value
  // sent is coerced to `playfield_draft` for the create payload.
  // Editing an already-Zoho-pushed DRAFT syncs to Zoho on every save;
  // editing a PLAYFIELD_DRAFT saves changes only (push is a separate
  // action from the detail view).
  const submitCreate = async (submitMode = "playfield_draft") => {
    if (!form.source_branch) return toast.error("Pick a source branch");
    if (!form.customer_id) return toast.error("Pick a customer from Zoho");

    // Detect the "typed-but-not-picked" row trap: a user pastes the SKU code
    // directly in the search box (or types it) and fills in Qty/Rate without
    // clicking a dropdown suggestion. The visual reads as if the row is
    // populated, but `item_id` is still empty — the search component only
    // commits the selection via `onSelect`. We surface a specific error
    // pointing them at the row so they know to pick from the dropdown.
    const typedNotPicked = rows.findIndex(
      (r) => !r.item_id && (Number(r.quantity) > 0 || r.rate || r.tax_id)
    );
    if (typedNotPicked !== -1) {
      return toast.error(
        `Row ${typedNotPicked + 1}: click an option from the search dropdown — typed text alone isn't a selection.`
      );
    }

    // Feb 2026 (AC7) — partition rows into (SKU line_items,
    // freight_lines). SKU rows require item_id; freight rows are
    // marked by `_is_freight=true` and carry description/amount.
    const _skuRows = rows.filter((r) => !r._is_freight && r.item_id && Number(r.quantity) > 0);
    const _freightRows = rows.filter((r) => r._is_freight && Number(r.rate) > 0);
    if (_skuRows.length === 0 && _freightRows.length === 0) return toast.error("Add at least one line item with quantity");
    const cleanRows = _skuRows;
    // Oct 2026 (AC1) — BUYER_SKU rate is server-authoritative and the
    // input is readOnly. Block submit with a clear, aggregated error
    // naming every unpriced SKU — not a soft badge next to a blank
    // field. For a two-line dispatch where neither SKU has a Price
    // Master row this collapses to the single toast "Cannot create
    // dispatch — no active Price Master entry for SKU-A, SKU-B".
    if (form.item_type === "BUYER_SKU") {
      const unlinked = cleanRows.filter((r) => r.buyer_link_missing);
      if (unlinked.length) {
        return toast.error(
          `Cannot create dispatch — this Zoho customer isn't linked to a Playfield buyer. Link the buyer, then retry. (Affects: ${unlinked.map((r) => r.item_id).join(", ")})`,
          { duration: 8000 }
        );
      }
      const unpriced = cleanRows.filter(
        (r) => r.price_master_missing || !(Number(r.rate) > 0),
      );
      if (unpriced.length) {
        return toast.error(
          `Cannot create dispatch — no active Price Master entry for: ${unpriced.map((r) => r.item_id).join(", ")}. Add a customer-specific price under Price Master and retry.`,
          { duration: 8000 }
        );
      }
    }
    for (const r of cleanRows) {
      if (!(Number(r.rate) >= 0)) return toast.error(`Rate required for ${r.item_id}`);
      if (!r.tax_id) return toast.error(`Pick a tax for ${r.item_id}`);
    }
    // Freight rows: amount required; tax_id optional (backend maps to
    // Freight Outwards account regardless).
    const _freightForBackend = _freightRows.map((r) => ({
      description: r.item_name || "Freight Outwards",
      amount: Number(r.rate) || 0,
      tax_id: r.tax_id || null,
      hsn_or_sac: r.hsn_or_sac || null,
    }));

    // Feb 2026 (reversed follow-up) — Transporter and Vehicle Number
    // are now fully optional on the standalone New Dispatch dialog.
    // The Dispatch Lots Send-to-Finance flow keeps its either/or rule
    // in `SendToFinanceDialog.jsx` — that path is untouched.

    setSubmitting(true);
    setBlockers(null);
    // Feb 2026 — dispatch_header capture (AC1/AC2). Sent verbatim on
    // both CREATE and EDIT. `null` when unset so the backend's
    // `_build_dispatch_header` returns None (AC4 — no header stamped)
    // for legacy-shaped submits that don't touch the header at all.
    const headerFields = {
      bill_to: form.bill_to || null,
      ship_to: form.ship_to || null,
      transporter: form.transporter_vendor_id ? { vendor_id: form.transporter_vendor_id } : null,
      vehicle_number: form.vehicle_number || null,
      // Feb 2026 (AC7) — freight lines now originate from the unified
      // line-items table (rows with `_is_freight=true`), partitioned
      // out at submit. Legacy `freightLines` state kept but unused.
      freight_lines: _freightForBackend,
    };
    try {
      let res;
      if (editingId) {
        // EDIT mode. Both PLAYFIELD_DRAFT and DRAFT now use the same
        // full-edit body (May 22 2026):
        //   - line_items: full replacement
        //   - source_branch / item_type / customer / header all editable
        //   - PLAYFIELD_DRAFT: saves changes only. Push to Zoho is a
        //     separate, explicit action (Feb 2026 decoupling).
        //   - DRAFT: backend pushes the new state to Zoho on every save.
        const payload = {
          source_branch: form.source_branch,
          item_type: form.item_type,
          customer_id: form.customer_id,
          customer_name: form.customer_name,
          date: form.date,
          reference_number: form.reference_number || null,
          notes: form.notes || null,
          place_of_supply: form.place_of_supply || null,
          discount: form.discount ? Number(form.discount) : null,
          discount_type: form.discount ? form.discount_type : null,
          line_items: cleanRows.map((r) => ({
            item_id: r.item_id,
            quantity: Number(r.quantity),
            rate: Number(r.rate),
            tax_id: r.tax_id,
          })),
          // Feb 2026 — always false. Coupled edit-then-push has been
          // removed; the backend will 400 if we send true. Kept in the
          // payload explicitly so an operator inspecting network calls
          // sees the flag they expect, set to the safe default.
          also_push_to_zoho: false,
          ...headerFields,
        };
        res = await axios.put(`${API}/dispatch-invoices/${editingId}`, payload, {
          headers: getHeaders(),
        });
        toast.success(
          res.data.message ||
            (editingStatus === "DRAFT" ? "Dispatch updated & synced to Zoho" : "Draft updated")
        );
      } else {
        const payload = {
          source_branch: form.source_branch,
          item_type: form.item_type,
          customer_id: form.customer_id,
          customer_name: form.customer_name,
          // Feb 2026 (dispatch invoice address-sync fix, AC1) — Playfield
          // buyer id, auto-fetched from `/buyer-addresses` when the
          // Zoho contact was picked. Skipping this only means the
          // backend does its own reverse lookup by `customer_zoho_id`
          // — same result, one extra query. `null` when the picked
          // Zoho contact has no linked Playfield buyer (transparent
          // fallback per AC3).
          buyer_id: headerBuyerId || null,
          date: form.date,
          reference_number: form.reference_number || null,
          notes: form.notes || null,
          place_of_supply: form.place_of_supply || null,
          discount: form.discount ? Number(form.discount) : null,
          discount_type: form.discount ? form.discount_type : null,
          line_items: cleanRows.map((r) => ({
            item_id: r.item_id,
            quantity: Number(r.quantity),
            rate: Number(r.rate),
            tax_id: r.tax_id,
          })),
          // Feb 2026 — every new dispatch is created as a Playfield
          // draft. Push to Zoho is a separate, explicit action from
          // the detail view (`POST /api/dispatches/{id}/push-to-zoho`).
          // The `submitMode` parameter above is retained for signature
          // compatibility but ignored — the server enforces the same
          // rule (400 if `mode="push_to_zoho"` is sent).
          mode: "playfield_draft",
          // Feb 2026: when the user picked a dispatch lot at the top of
          // the dialog, the backend enforces strict line/customer match.
          source_lot_id: selectedLot ? selectedLot.lot_id : null,
          ...headerFields,
        };
        res = await axios.post(`${API}/dispatch-invoices`, payload, {
          headers: getHeaders(),
        });

        // Feb 2026: backend returns 202-equivalent `{status:"pending", job_id}`
        // when the Zoho push exceeds an 8s budget. Surface this to the user
        // with a polling-until-done loop so they don't have to refresh.
        if (res?.data?.status === "pending" && res?.data?.job_id) {
          const jobId = res.data.job_id;
          toast.success(
            res.data.message ||
              "Dispatch saved — Zoho invoice is being pushed in the background."
          );
          // Close dialog optimistically; the list will be refreshed once the
          // job lands. (`PUSH_PENDING` rows are visible in the list while
          // the orchestrator is still running.)
          setShowCreate(false);
          setEditingId(null);
          setEditingStatus(null);
          resetCreate();
          if (!editingId) setPage(1);
          fetchDispatches();
          try {
            const final = await pollJobUntilDone(jobId);
            if (final.status === "COMPLETED") {
              const r = final.result || {};
              toast.success(
                r.zoho_invoice_number
                  ? `Zoho invoice ${r.zoho_invoice_number} created`
                  : "Dispatch pushed to Zoho Books"
              );
            } else {
              toast.error(
                `Zoho push failed: ${final.error || "unknown error"}`
              );
            }
          } catch (pollErr) {
            if (pollErr?.reason === "timeout") {
              toast.message(
                "Still pushing to Zoho — check back in a minute. Status will update on the list automatically."
              );
            } else {
              toast.error(`Could not track Zoho push: ${pollErr.message || pollErr}`);
            }
          } finally {
            fetchDispatches();
          }
          setSubmitting(false);
          return;
        }

        toast.success(res.data.message || "Dispatch created");
      }
      setShowCreate(false);
      setEditingId(null);
      setEditingStatus(null);
      resetCreate();
      if (!editingId) setPage(1);
      fetchDispatches();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        if (detail.code === "INSUFFICIENT_STOCK") {
          toast.error(detail.message || "Insufficient stock");
          setBlockers({ type: "stock", items: detail.insufficient_items || [] });
        } else if (detail.code === "ITEM_MASTER_INCOMPLETE") {
          toast.error("Some items are missing master data needed by Zoho");
          setBlockers({ type: "master", items: detail.items || [] });
        } else if (detail.code === "PRICE_MASTER_MISSING") {
          toast.error(detail.message || "Set price in Price Master first");
          setBlockers({ type: "price_master", items: detail.items || [] });
        } else if (detail.code === "LOT_NOT_FREE") {
          toast.error(detail.message || "Dispatch lot is no longer available");
          // Clear the stale lot selection so the user can pick another.
          setSelectedLot(null);
        } else if (detail.code === "LOT_LINES_MISMATCH") {
          toast.error("Lot lines don't match — re-select the lot to refresh");
          setBlockers({ type: "lot_lines_mismatch", items: detail.mismatches || [] });
        } else if (detail.code === "LOT_CUSTOMER_MISMATCH") {
          toast.error(detail.message || "Customer doesn't match the lot");
        } else {
          toast.error(detail.message || "Failed to create dispatch");
        }
      } else {
        toast.error(detail || err.message || "Failed to create dispatch");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Persist the row's current rate (+ tax_id) as the new latest sale price
  // for this (customer, item) pair. POST /api/sale-prices/upsert.
  //   - Confirms with the user if a prior saleprice row exists (per spec).
  //   - Updates `rate_source` to `customer_saleprice` on success so the
  //     badge flips immediately and future Update Price clicks for the
  //     same row also confirm.
  const handleUpdatePrice = async (idx) => {
    const r = rows[idx];
    if (!r?.item_id) return toast.error("Pick an item first");
    if (!form.customer_id) return toast.error("Pick a customer first");
    if (!(Number(r.rate) > 0)) return toast.error("Enter a rate > 0");
    if (!r.tax_id) return toast.error("Pick a tax");

    // Confirm only if a prior customer-saleprice already exists for this row.
    // Spec: "If a price already exists and the user tries to update, display
    // a message ... before the update."
    if (r.rate_source === "customer_saleprice") {
      const ok = window.confirm(
        "This action will update all future prices for this customer for this item. Continue?"
      );
      if (!ok) return;
    }

    try {
      const res = await axios.post(
        `${API}/sale-prices/upsert`,
        {
          customer_zoho_id: form.customer_id,
          customer_name: form.customer_name,
          item_type: form.item_type,
          item_id: r.item_id,
          rate: Number(r.rate),
          tax_id: r.tax_id,
        },
        { headers: getHeaders() }
      );
      const body = res.data || {};
      // Flip the badge to "Customer rate" so subsequent edits prompt the
      // overwrite-confirm dialog.
      setRow(idx, { rate_source: "customer_saleprice" });
      if (body.first_time) {
        toast.success(`Price ₹${body.new_rate} saved for ${form.customer_name} · ${r.item_id}`);
      } else {
        toast.success(
          `Price updated: ₹${body.previous_rate} → ₹${body.new_rate} for ${form.customer_name} · ${r.item_id}`
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) ||
          (detail && detail.message) ||
          err.message ||
          "Failed to save price"
      );
    }
  };

  // Delete a PLAYFIELD_DRAFT (releases inventory). Closes the detail dialog
  // on success and refetches the list. Backend rejects DELETE on any other
  // status with 409.
  const handleDelete = async (d) => {
    if (!d?.id) return;
    const status = (d.status || "").toUpperCase();
    const confirmMsg = status === "REJECTED"
      ? `Delete rejected dispatch ${d.dispatch_code}? (Stock was already released by the reject lifecycle; this only hides it from the default list.)`
      : `Delete dispatch ${d.dispatch_code}? Locked inventory will be released.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.delete(`${API}/dispatch-invoices/${d.id}`, { headers: getHeaders() });
      toast.success(res.data.message || "Dispatch deleted");
      setShowDetail(false);
      setSelected(null);
      fetchDispatches();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete dispatch");
    }
  };

  // Push a PLAYFIELD_DRAFT to Zoho without opening the edit dialog. Used by
  // the detail dialog's quick action. Stock stays deducted; status flips to
  // DRAFT and the Zoho invoice id is attached.
  //
  // May 2026 — handles the orchestrator's bounded-wait protocol:
  //   • HTTP 200 with status="DRAFT" → finished within 8s, refresh and done.
  //   • HTTP 202 with status="pending" + job_id → slow Zoho call, poll
  //     `/api/jobs/{job_id}` via pollJobUntilDone. Surface progress as a
  //     loading toast; resolve with the final job status (COMPLETED →
  //     success toast + refresh; FAILED → error toast with the job's
  //     error message).
  const handlePushToZoho = async (d) => {
    if (!d?.id) return;
    // Feb 2026 — same handler now drives BOTH "Push to Zoho" (for
    // PLAYFIELD_DRAFT / PUSH_FAILED) and "Sync to Zoho" (for
    // DRAFT_UNSYNCED). Endpoint depends on the current status.
    const isSync = (d.status || "").toUpperCase() === "DRAFT_UNSYNCED";
    const verb = isSync ? "Syncing" : "Pushing";
    const successVerb = isSync ? "Synced" : "Pushed";
    const endpoint = isSync ? "sync-to-zoho" : "push-to-zoho";
    const tid = toast.loading(`${verb} ${d.dispatch_code || ""} to Zoho…`);
    try {
      const res = await axios.post(
        `${API}/dispatch-invoices/${d.id}/${endpoint}`,
        {},
        { headers: getHeaders() }
      );
      if (res.status === 202 && res.data?.job_id) {
        // Slow Zoho — poll until the background task finishes.
        try {
          const job = await pollJobUntilDone(res.data.job_id, {
            onTick: () =>
              toast.loading(`${verb} ${d.dispatch_code || ""} to Zoho…`, { id: tid }),
          });
          if (job.status === "COMPLETED") {
            toast.success(
              `${successVerb} ${d.dispatch_code || ""} to Zoho (invoice ${
                job.result?.invoice_number || ""
              }).`,
              { id: tid }
            );
            setShowDetail(false);
            setSelected(null);
            fetchDispatches();
          } else {
            toast.error(
              `${verb} to Zoho failed: ${job.error || "unknown error"}`,
              { id: tid }
            );
            fetchDispatches();
          }
        } catch (pollErr) {
          if (pollErr?.reason === "timeout") {
            toast.message(
              `Still ${verb.toLowerCase()} — check the dispatch list in a minute. ` +
                "The background task will finish or report its error.",
              { id: tid }
            );
            fetchDispatches();
          } else {
            toast.error(`Poll failed: ${pollErr.message || pollErr}`, { id: tid });
          }
        }
        return;
      }
      toast.success(res.data.message || `${successVerb} to Zoho`, { id: tid });
      setShowDetail(false);
      setSelected(null);
      fetchDispatches();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (detail && typeof detail === "object" ? detail.message : detail) ||
          err.message ||
          `${verb} to Zoho failed`,
        { id: tid }
      );
      // Refresh so any PUSH_FAILED badge becomes visible.
      fetchDispatches();
    }
  };

  // ---------- Render ----------
  // Lines and qty are now mutable in both CREATE mode and edit-of-any-draft
  // (May 22 2026 — Zoho Drafts can also be fully edited; the change is
  // pushed through to Zoho via update_invoice with inventory-delta rollback
  // on failure).
  const linesLocked = false;
  const totalRowsSumQty = rows
    .filter((r) => Number(r.quantity) > 0)
    .reduce((s, r) => s + Number(r.quantity), 0);

  // ---- Per-line + invoice totals (mirrors RM Inward UX) -----------------
  // Apply the bill-level discount BEFORE tax. Tax for each line is
  // computed on its share of (qty×rate − line_share_of_discount).
  const _taxPctById = useMemo(() => {
    const m = {};
    for (const t of zohoTaxes) m[t.tax_id] = Number(t.tax_percentage || 0);
    return m;
  }, [zohoTaxes]);

  const lineGross = (r) =>
    Number(r.quantity) > 0 && Number(r.rate) >= 0
      ? Number(r.quantity) * Number(r.rate)
      : 0;

  const subTotal = rows.reduce((s, r) => s + lineGross(r), 0);

  const discountAmount = useMemo(() => {
    const d = Number(form.discount) || 0;
    if (d <= 0) return 0;
    if (form.discount_type === "percentage") {
      return Math.min(subTotal, (subTotal * d) / 100);
    }
    return Math.min(subTotal, d);
  }, [form.discount, form.discount_type, subTotal]);

  // Distribute discount pro-rata across lines so each line's tax base
  // is correct (matches Zoho's `is_discount_before_tax=true` semantics).
  const linesWithTax = rows.map((r) => {
    const gross = lineGross(r);
    const share = subTotal > 0 ? (gross / subTotal) * discountAmount : 0;
    const taxable = Math.max(0, gross - share);
    const pct = _taxPctById[r.tax_id] || 0;
    const tax = (taxable * pct) / 100;
    return { gross, share, taxable, pct, tax };
  });
  const taxTotal = linesWithTax.reduce((s, l) => s + l.tax, 0);
  const grandTotal = subTotal - discountAmount + taxTotal;
  const fmtINR = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="p-6 space-y-6" data-testid="dispatch-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <Truck className="w-8 h-8" />
            Dispatch
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Dispatch RM or Buyer SKUs from branch stock via Zoho Books invoices
          </p>
        </div>
        <PermButton entity="Dispatch" action="CREATE" onClick={openCreate} data-testid="dispatch-create-btn" className="gap-2">
          <Plus className="w-4 h-4" />
          Create Dispatch
        </PermButton>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Status</Label>
            <Select value={filterStatus} onValueChange={(v) => { setPage(1); setFilterStatus(v); }}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="PLAYFIELD_DRAFT">Playfield Draft</SelectItem>
                <SelectItem value="DRAFT">Zoho Draft</SelectItem>
                <SelectItem value="DRAFT_UNSYNCED">Zoho Draft — Not Synced</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
                <SelectItem value="DELETED">Deleted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Source Branch</Label>
            <Select value={filterSourceBranch} onValueChange={(v) => { setPage(1); setFilterSourceBranch(v); }}>
              <SelectTrigger data-testid="filter-source-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Customer</Label>
            <Select value={filterCustomer} onValueChange={(v) => { setPage(1); setFilterCustomer(v); }}>
              <SelectTrigger data-testid="filter-customer"><SelectValue placeholder="All Customers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Customers</SelectItem>
                {customers.slice(0, 500).map((c) => (
                  <SelectItem key={c.id} value={c.name} data-testid={`filter-customer-opt-${c.id}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Date From</Label>
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => { setPage(1); setFilterDateFrom(e.target.value); }}
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Date To</Label>
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => { setPage(1); setFilterDateTo(e.target.value); }}
              data-testid="filter-date-to"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => { setFilterStatus("ALL"); setFilterSourceBranch("ALL"); setFilterCustomer("ALL"); setFilterDateFrom(""); setFilterDateTo(""); setPage(1); }}
              data-testid="filter-reset"
            >
              <X className="w-4 h-4" /> Reset
            </Button>
            <Button variant="outline" onClick={fetchDispatches} data-testid="dispatch-refresh"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider">
            Dispatches ({total})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-zinc-500">
              <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" /> Loading...
            </div>
          ) : dispatches.length === 0 ? (
            <div className="p-8 text-center text-zinc-500" data-testid="dispatch-empty">
              No dispatches yet. Click "Create Dispatch" to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="dispatch-table">
                <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-600 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">Code</th>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Source</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-right">Items</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatches.map((d) => (
                    <tr key={d.id} className="border-b hover:bg-zinc-50" data-testid={`dispatch-row-${d.dispatch_code}`}>
                      <td className="px-4 py-3 font-mono font-semibold">{d.dispatch_code}</td>
                      <td className="px-4 py-3">{d.date}</td>
                      <td className="px-4 py-3">{d.source_branch}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {d.item_type === "RM" ? "RM" : "Buyer SKU"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">{d.customer_name}</td>
                      <td className="px-4 py-3 text-right">{d.line_items?.length || 0}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        ₹{Number(d.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${STATUS_COLORS[d.status] || ""} border`}>
                            {STATUS_LABELS[d.status] || d.status}
                          </Badge>
                          <ZohoPushStatusBadge
                            status={d.zoho_push_status}
                            error={d.zoho_push_error}
                            retryCount={d.zoho_push_retry_count || 0}
                            testIdPrefix={`dispatch-${d.dispatch_code}`}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          {(d.status === "PLAYFIELD_DRAFT" ||
                            d.status === "PUSH_FAILED" ||
                            (d.status === "DRAFT" && d.zoho_invoice_id)) && (
                            <PermButton
                              entity="Dispatch" action="UPDATE"
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(d)}
                              data-testid={`dispatch-edit-${d.dispatch_code}`}
                              title={
                                d.status === "PLAYFIELD_DRAFT"
                                  ? "Edit draft (not in Zoho yet)"
                                  : d.status === "PUSH_FAILED"
                                  ? "Edit failed push — fix and retry"
                                  : "Edit invoice (syncs to Zoho)"
                              }
                            >
                              <Pencil className="w-4 h-4" />
                            </PermButton>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelected(d); setShowDetail(true); }}
                            data-testid={`dispatch-view-${d.dispatch_code}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination — inside the Card, mirrors the IBT pattern.
              Always visible whenever there's at least one row of data so
              users can see WHICH page they're on even when total ≤ PAGE_SIZE. */}
          {total > 0 && (
            <div className="flex items-center justify-between p-3 border-t text-sm" data-testid="dispatch-pagination">
              <div className="text-zinc-500">
                Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                {" "}· {PAGE_SIZE} per page · {total} total
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                  data-testid="pagination-first"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="pagination-prev"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="pagination-next"
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  data-testid="pagination-last"
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {editingId && (
        <BookVehicleDialog
          open={bookVehicleOpen}
          onClose={() => setBookVehicleOpen(false)}
          sourceType="DISPATCH_DRAFT"
          sourceId={editingId}
          sourceLabel={`Dispatch draft ${editingId}`}
        />
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!submitting) { setShowCreate(open); if (!open) setEditingId(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-create-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? editingStatus === "PLAYFIELD_DRAFT"
                  ? "Edit Dispatch (Playfield Draft)"
                  : editingStatus === "PUSH_FAILED"
                  ? "Edit Dispatch (Push Failed — fix & retry)"
                  : "Edit Dispatch (Zoho Draft)"
                : "Create Dispatch"}
            </DialogTitle>
            <DialogDescription>
              {editingId ? (
                editingStatus === "PLAYFIELD_DRAFT" ? (
                  <>
                    Playfield-only draft — not yet in Zoho. Lines, quantities,
                    source branch, item type, and customer are all editable.
                    Inventory is re-locked to match the new lines.
                  </>
                ) : editingStatus === "PUSH_FAILED" ? (
                  <>
                    Zoho rejected the previous push. Fix the issue (typically
                    tax shape, line items, or customer), save the draft, and
                    click <em>Push to Zoho</em> again to retry. Inventory stays
                    locked across the edit; saving reverts the row to a clean
                    Playfield Draft state.
                  </>
                ) : (
                  <>
                    Editing a Zoho draft. Lines, quantities, source branch,
                    item type, and customer are all editable — every save is
                    pushed to Zoho. If Zoho rejects the change, the inventory
                    delta is rolled back automatically.
                  </>
                )
              ) : (
                <>
                  Save as a Playfield draft — stock is locked at the source
                  branch immediately. Pushing to Zoho is a separate action
                  from the dispatch&apos;s detail view once the draft exists.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Source + Type — fully editable in both create and edit
                modes. For DRAFT edits the change is sent to Zoho along with
                everything else; if Zoho rejects, the inventory delta is
                rolled back automatically. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Source Branch *</Label>
                <Select
                  value={form.source_branch}
                  onValueChange={(v) => setForm({ ...form, source_branch: v })}
                  disabled={!!selectedLot && false /* source_branch stays editable per user direction (a) — lot has no branch */}
                >
                  <SelectTrigger data-testid="dispatch-source-branch">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Item Type *</Label>
                <Select
                  value={form.item_type}
                  onValueChange={(v) => { setForm({ ...form, item_type: v }); setRows([{ item_id: "", quantity: "", rate: "", tax_id: "", item_name: "", item_unit: null, item_stock: null, rate_source: null }]); }}
                  disabled={!!selectedLot /* lot is always BUYER_SKU — block accidental switch */}
                >
                  <SelectTrigger data-testid="dispatch-item-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RM">Raw Material</SelectItem>
                    <SelectItem value="BUYER_SKU">Buyer SKU (FG)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Invoice Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="dispatch-date"
                />
              </div>
            </div>

            {/* Feb 2026 — Source Dispatch Lot picker. Visible at the TOP
                of every CREATE dialog so users discover the workflow even
                when Item Type is still on the RM default. Lot picks are
                only valid for Buyer SKU dispatches, so selecting a lot
                AUTO-FLIPS `item_type` to BUYER_SKU before auto-filling
                customer + lines. */}
            {!editingId && (
              <div className="border rounded p-3 bg-zinc-50/50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-semibold">Source Dispatch Lot (optional)</Label>
                  {selectedLot && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedLot(null);
                        setForm((f) => ({ ...f, customer_id: "", customer_name: "" }));
                        setCustQuery("");
                        setRows([_blankRow()]);
                        setLotQuery("");
                      }}
                      data-testid="dispatch-lot-clear"
                    >
                      <X className="w-4 h-4 mr-1" /> Clear lot
                    </Button>
                  )}
                </div>

                {selectedLot ? (
                  <div className="space-y-2" data-testid="dispatch-lot-selected">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                        {selectedLot.lot_number}
                      </Badge>
                      <span className="text-sm text-zinc-600">
                        {selectedLot.customer_name} · {selectedLot.line_count} line(s) · {selectedLot.total_quantity} unit(s)
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 italic">
                      Customer and line items are locked. Pick a different lot or
                      clear to start over.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
                      <Input
                        placeholder="Search PENDING_FINANCE lots by lot number, customer, or buyer SKU…"
                        value={lotQuery}
                        onChange={(e) => setLotQuery(e.target.value)}
                        className="pl-9"
                        data-testid="dispatch-lot-search"
                      />
                    </div>
                    {(lotLoading || lotOptions.length > 0) && (
                      <div className="border rounded mt-1 max-h-56 overflow-y-auto bg-white shadow-sm" data-testid="dispatch-lot-results">
                        {lotLoading && (
                          <div className="p-2 text-sm text-zinc-500 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                          </div>
                        )}
                        {!lotLoading && lotOptions.length === 0 && (
                          <div className="p-2 text-sm text-zinc-500">No matching lots</div>
                        )}
                        {!lotLoading && lotOptions.map((lot) => (
                          <button
                            key={lot.lot_id}
                            type="button"
                            className="w-full text-left p-2 hover:bg-zinc-50 border-b last:border-0 text-sm"
                            onClick={async () => {
                              setSelectedLot(lot);
                              // AUTO-SWITCH to BUYER_SKU. Lots only carry
                              // buyer-SKU lines so we'd otherwise render
                              // an unusable RM picker.
                              // Feb 2026 bug fix — resolve the lot's
                              // buyer to a REAL Zoho contact id via
                              // /dispatch-invoices/lot-buyer-zoho. Never
                              // copy `lot.customer_id` (Playfield code)
                              // into form.customer_id — that used to
                              // land unchanged in dispatch_invoices.
                              // customer_zoho_id, which Zoho then
                              // rejected on push.
                              let resolvedZohoId = "";
                              let resolvedName = lot.customer_name || "";
                              try {
                                const r = await axios.get(
                                  `${API}/dispatch-invoices/lot-buyer-zoho?lot_id=${encodeURIComponent(lot.lot_id)}`,
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                resolvedZohoId = r?.data?.customer_zoho_id || "";
                                resolvedName = r?.data?.buyer_name || resolvedName;
                                if (!resolvedZohoId) {
                                  toast.warning(
                                    `Buyer "${resolvedName}" isn't linked to a Zoho contact yet — please pick their Zoho customer manually.`,
                                    { duration: 6000 }
                                  );
                                }
                              } catch (err) {
                                // Non-fatal — leave the field empty so
                                // the user searches manually. Never
                                // fall back to `lot.customer_id`.
                                toast.warning(
                                  "Couldn't resolve the lot's Zoho customer link — please pick the customer manually.",
                                  { duration: 6000 }
                                );
                              }
                              setForm((f) => ({
                                ...f,
                                item_type: "BUYER_SKU",
                                customer_id: resolvedZohoId,
                                customer_name: resolvedZohoId ? resolvedName : "",
                              }));
                              setCustQuery("");
                              setRows(
                                (lot.lines || []).map((li) => ({
                                  _key: _newRowKey(),
                                  item_id: li.buyer_sku_id,
                                  item_name: li.sku_name,
                                  quantity: String(li.quantity),
                                  rate: "",
                                  tax_id: "",
                                  item_unit: null,
                                  item_stock: null,
                                  rate_source: null,
                                  locked_from_lot: true,
                                  original_qty: Number(li.quantity || 0),
                                }))
                              );
                              setLotOptions([]);
                              setLotQuery("");
                              if (resolvedZohoId) {
                                toast.success(`Lot ${lot.lot_number} loaded — customer + lines locked to ${resolvedName}`);
                              } else {
                                toast.success(`Lot ${lot.lot_number} lines loaded — customer needs a manual pick`);
                              }
                            }}
                            data-testid={`dispatch-lot-option-${lot.lot_id}`}
                          >
                            <div className="font-medium">{lot.lot_number}</div>
                            <div className="text-xs text-zinc-500">
                              {lot.customer_name} · {lot.line_count} line(s) · {lot.total_quantity} unit(s)
                              {lot.sent_to_finance_at && (
                                <> · sent {new Date(lot.sent_to_finance_at).toLocaleDateString()}</>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-zinc-500 mt-2">
                      Optional. Pick a lot to auto-fill + lock the customer and
                      line items (auto-switches Item Type to Buyer SKU), or leave blank to dispatch ad-hoc.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Customer search */}
            <div>
              <Label>Customer (from Zoho) *</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
                <Input
                  placeholder={selectedLot
                    ? "Locked by selected lot — clear lot to change"
                    : "Type at least 2 characters to search Zoho customers"}
                  value={form.customer_id ? form.customer_name : custQuery}
                  onChange={(e) => {
                    setCustQuery(e.target.value);
                    if (form.customer_id) setForm({ ...form, customer_id: "", customer_name: "" });
                  }}
                  className="pl-9"
                  disabled={!!selectedLot}
                  data-testid="dispatch-customer-search"
                />
                {form.customer_id && !selectedLot && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1"
                    onClick={() => { setForm({ ...form, customer_id: "", customer_name: "" }); setCustQuery(""); }}
                    data-testid="dispatch-customer-clear"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {custQuery.length >= 2 && !form.customer_id && (
                <div className="border rounded mt-1 max-h-48 overflow-y-auto bg-white shadow-sm" data-testid="dispatch-customer-results">                  {custSearching && (
                    <div className="p-2 text-sm text-zinc-500 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Searching...
                    </div>
                  )}
                  {!custSearching && custResults.length === 0 && (
                    <div className="p-2 text-sm text-zinc-500">No matching customers</div>
                  )}
                  {custResults.map((c) => (
                    <button
                      key={c.contact_id}
                      type="button"
                      className="w-full text-left p-2 hover:bg-zinc-50 border-b last:border-0 text-sm"
                      onClick={() => {
                        setForm({ ...form, customer_id: c.contact_id, customer_name: c.contact_name });
                        setCustQuery("");
                        setCustResults([]);
                        // Feb 2026 — dispatch_header prefill (AC1). Fetch
                        // buyer master addresses matched by customer_zoho_id.
                        // Falls back gracefully to empty lists (manual
                        // entry mode) for unlinked buyers.
                        (async () => {
                          try {
                            const res = await axios.get(
                              `${API}/dispatch-invoices/buyer-addresses?customer_zoho_id=${encodeURIComponent(c.contact_id)}`,
                              { headers: getHeaders() },
                            );
                            const bl = res.data?.buyer_addresses?.bill_to || [];
                            const sh = res.data?.buyer_addresses?.ship_to || [];
                            setHeaderBillToOptions(bl);
                            setHeaderShipToOptions(sh);
                            setHeaderBuyerMatched(res.data?.buyer?.matched || false);
                            setHeaderBuyerName(res.data?.buyer?.name || "");
                            setHeaderBuyerCode(res.data?.buyer?.code || "");
                            setHeaderBuyerId(res.data?.buyer?.id || "");
                            // Auto-select default entries (first in the
                            // list because backend returns default-first).
                            setForm((prev) => ({
                              ...prev,
                              bill_to: bl[0] || null,
                              ship_to: sh[0] || null,
                            }));
                          } catch {
                            setHeaderBillToOptions([]);
                            setHeaderShipToOptions([]);
                            setHeaderBuyerMatched(false);
                            setHeaderBuyerName("");
                            setHeaderBuyerCode("");
                            setHeaderBuyerId("");
                          }
                        })();
                      }}
                      data-testid={`customer-pick-${c.contact_id}`}
                    >
                      <div className="font-semibold">{c.contact_name}</div>
                      {c.company_name && <div className="text-xs text-zinc-500">{c.company_name}</div>}
                      {c.gst_no && <div className="text-xs font-mono text-zinc-400">GST: {c.gst_no}</div>}
                    </button>
                  ))}
                </div>
              )}
              {/* Feb 2026 (dispatch invoice address-sync fix, AC4) —
                  read-only strip that displays which Playfield buyer
                  record the picked Zoho contact resolves to. When a
                  match exists the operator sees "Playfield Buyer:
                  <name> · <code>". When it doesn't, a soft warning
                  makes it explicit that the push will fall back to
                  the Zoho contact's default address. Auto-fetched
                  from `/buyer-addresses`; never editable. */}
              {form.customer_id && headerBuyerMatched && (
                <div
                  className="mt-1.5 text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-2 py-1"
                  data-testid="dispatch-header-playfield-buyer"
                >
                  <span className="font-semibold text-zinc-500 uppercase tracking-wide">
                    Playfield Buyer:
                  </span>{' '}
                  <span className="font-medium text-zinc-800">{headerBuyerName || "—"}</span>
                  {headerBuyerCode && (
                    <>
                      {' '}
                      <span className="text-zinc-400">·</span>{' '}
                      <span className="font-mono text-zinc-700" data-testid="dispatch-header-playfield-buyer-code">
                        {headerBuyerCode}
                      </span>
                    </>
                  )}
                </div>
              )}
              {form.customer_id && !headerBuyerMatched && (
                <div
                  className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                  data-testid="dispatch-header-playfield-buyer-unmatched"
                >
                  No linked Playfield buyer — Zoho contact default will be used.
                </div>
              )}
            </div>

            {/* Feb 2026 — dispatch_header capture (AC1). Shown once a
                customer has been picked. Reuses the same AddressBlock
                component used by LotSubmittedViewerDialog so the read-
                only address display is identical between the two
                creation paths. Selectors appear only when the buyer
                has >1 address of a given type. If no local buyer
                master is linked (`headerBuyerMatched=false`), the
                block falls back to a "no linked buyer master" hint —
                the fields are still captured on submit as free-text
                (form.bill_to / form.ship_to remain the source of
                truth for the payload). */}
            {form.customer_id && (
              <>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dispatch-header-section">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs font-bold uppercase">Bill To</Label>
                      {headerBillToOptions.length > 1 && (
                        <Select
                          value={form.bill_to?.address_id || ""}
                          onValueChange={(v) => setForm({ ...form, bill_to: headerBillToOptions.find((a) => a.address_id === v) || null })}
                        >
                          <SelectTrigger className="h-7 w-[190px] text-xs" data-testid="dispatch-bill-to-selector">
                            <SelectValue placeholder="Pick address" />
                          </SelectTrigger>
                          <SelectContent>
                            {headerBillToOptions.map((a) => (
                              <SelectItem key={a.address_id} value={a.address_id}>
                                {a.label || a.line1 || a.address_id} {a.is_default ? "★" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <AddressBlock label="Bill To" addr={form.bill_to} testIdPrefix="dispatch-bill-to" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs font-bold uppercase">Ship To</Label>
                      {headerShipToOptions.length > 1 && (
                        <Select
                          value={form.ship_to?.address_id || ""}
                          onValueChange={(v) => setForm({ ...form, ship_to: headerShipToOptions.find((a) => a.address_id === v) || null })}
                        >
                          <SelectTrigger className="h-7 w-[190px] text-xs" data-testid="dispatch-ship-to-selector">
                            <SelectValue placeholder="Pick address" />
                          </SelectTrigger>
                          <SelectContent>
                            {headerShipToOptions.map((a) => (
                              <SelectItem key={a.address_id} value={a.address_id}>
                                {a.label || a.line1 || a.address_id} {a.is_default ? "★" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <AddressBlock label="Ship To" addr={form.ship_to} testIdPrefix="dispatch-ship-to" />
                  </div>
                </div>
                {form.customer_id && !headerBuyerMatched && !form.bill_to && !form.ship_to && (
                  <div className="text-[11px] text-zinc-500 italic -mt-1" data-testid="dispatch-header-no-buyer-hint">
                    No local buyer master linked to this Zoho contact — addresses will be captured as blank. Add the buyer via Tech Ops → Buyers to auto-populate next time.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-bold uppercase">Transporter</Label>
                    <Select
                      value={form.transporter_vendor_id || ""}
                      onValueChange={(v) => setForm({ ...form, transporter_vendor_id: v })}
                    >
                      <SelectTrigger data-testid="dispatch-transporter-select">
                        <SelectValue placeholder="Select transporter" />
                      </SelectTrigger>
                      <SelectContent>
                        {transporters.length === 0 && (
                          <div className="p-2 text-xs text-zinc-500">No transporter vendors — add one via Vendors.</div>
                        )}
                        {transporters.map((v) => (
                          <SelectItem key={v.id || v.vendor_id} value={v.id || v.vendor_id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase">Vehicle Number</Label>
                    <Input
                      value={form.vehicle_number}
                      onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })}
                      placeholder="e.g. MH12AB1234"
                      data-testid="dispatch-vehicle-number-input"
                    />
                  </div>
                </div>
                {/* Feb 2026 (reversed follow-up) — Transporter and
                    Vehicle Number are now fully optional on the
                    standalone New Dispatch dialog. Amber hint removed. */}
              </>
            )}

            {/* Feb 2026 (AC7) — the standalone "Freight Lines" section
                was removed. Freight rows for lot-linked dispatches
                now live inline in the main Line Items table below,
                marked with a FREIGHT badge and partitioned back into
                `dispatch_header.freight_lines` at submit time. */}
            {/* Line items */}
            <Separator />
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Line Items *</Label>
                {editingId && (
                  <BookVehicleButton onClick={() => setBookVehicleOpen(true)} />
                )}
              </div>
              {!form.source_branch && (
                <div className="text-xs text-zinc-500 mb-2">Pick a source branch to see available stock</div>
              )}
              {itemOptionsLoading && (
                <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading stock...
                </div>
              )}
              <div className="space-y-2">
                {rows.map((r, idx) => {
                  // "Service line" covers both Freight and Transportation
                  // Cost — every downstream UX guard (qty lock, no stock
                  // warn, service-charge banner) applies to both.
                  const isFreight =
                    r.is_freight
                    || r.is_transportation_cost
                    || r.item_id === "FREIGHT"
                    || r.item_id === "TRANSPORTATION_COST";
                  // A row is "original" if it was loaded from an existing
                  // draft and the user hasn't changed its item. Quantity
                  // edits on original rows are reconciled by the backend
                  // at save time (delta math against post-restore stock),
                  // so we skip the inline stock-warn for them — otherwise
                  // a draft that fully drew down inventory would be locked
                  // (user couldn't even REDUCE the qty).
                  const isOriginalRow = r.original_qty !== undefined;
                  // Feb 2026: rows seeded from a picked dispatch lot are
                  // hard-locked — buyer SKU and quantity can't be edited
                  // (the finance ⇒ logistics handoff treats them as
                  // immutable). Rate / tax_id stay editable so logistics
                  // can attach customer-saleprice + GST tax.
                  const rowLocked = linesLocked || !!r.locked_from_lot;
                  const opt = itemOptions.find((o) => o.item_id === r.item_id);
                  const stockBasis = isFreight ? null : (opt?.current_stock ?? r.item_stock);
                  const qtyNum = Number(r.quantity);
                  const stockWarn = !isFreight && !isOriginalRow && stockBasis != null && qtyNum > Number(stockBasis);
                  return (
                    <div key={r._key} className="border rounded p-3 space-y-2 bg-zinc-50/50" data-testid={`dispatch-row-${idx}`}>
                      <div className="grid grid-cols-12 gap-2 items-start">
                        <div className="col-span-11">
                          {r._is_freight ? (
                            <div className="border rounded px-3 py-2 bg-amber-50 font-mono text-sm flex items-center gap-2" data-testid={`dispatch-row-item-${idx}`}>
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-200 text-amber-900"
                                data-testid={`dispatch-row-freight-badge-${idx}`}
                              >
                                Freight
                              </span>
                              <Input
                                className="flex-1"
                                value={r.item_name || ""}
                                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, item_name: e.target.value } : x))}
                                placeholder="Description (e.g. Transportation Cost)"
                                data-testid={`dispatch-row-freight-desc-${idx}`}
                              />
                            </div>
                          ) : rowLocked ? (
                            <div className="border rounded px-3 py-2 bg-zinc-100 font-mono text-sm" data-testid={`dispatch-row-item-${idx}`}>
                              {r.item_id} <span className="text-xs text-zinc-500">{r.item_name ? `— ${r.item_name}` : ""}</span>
                              {r.locked_from_lot && (
                                <span className="ml-2 text-xs text-blue-600">(from lot)</span>
                              )}
                            </div>
                          ) : (
                            <StockItemSearch
                              branch={form.source_branch}
                              type={form.item_type === "RM" ? "RM" : "FG"}
                              value={r.item_id}
                              disabled={!form.source_branch}
                              placeholder={
                                form.item_type === "RM"
                                  ? "Search Raw Material or 'Freight' (min 2 chars)"
                                  : "Search Buyer SKU or 'Freight' (min 2 chars)"
                              }
                              includeFreight={true}
                              excludeIds={rows.filter((_, i) => i !== idx).map((rr) => rr.item_id).filter(Boolean)}
                              // Off-stock fallback: when /stock-search returns 0
                              // (because the SKU has zero FG inventory at this
                              // branch), call /dispatch-invoices/item-resolve to
                              // surface the master record so the user can still
                              // pick it. Without this the dispatch row stays
                              // blank with "No prior price" even though the SKU
                              // exists and has a price_master rate.
                              resolveItem={async (q) => {
                                if (!form.source_branch || !form.item_type) return null;
                                try {
                                  const params = new URLSearchParams({
                                    source_branch: form.source_branch,
                                    item_type: form.item_type,
                                    item_id: q,
                                  });
                                  if (form.customer_id) params.set("customer_id", form.customer_id);
                                  const res = await axios.get(
                                    `${API}/dispatch-invoices/item-resolve?${params.toString()}`,
                                  );
                                  return res.data && res.data.found ? res.data : null;
                                } catch {
                                  return null;
                                }
                              }}
                              onSelect={(item) => {
                                if (!item.item_id) {
                                  setRow(idx, { item_id: "", rate: "" });
                                } else {
                                  pickItem(idx, item.item_id, item);
                                }
                              }}
                              testId={`dispatch-row-item-${idx}`}
                            />
                          )}
                          {isFreight ? (
                            <div className="text-xs text-amber-700 mt-1 font-medium">
                              Service charge — qty locked at 1, no stock impact
                            </div>
                          ) : (
                            (opt || r.item_stock != null) && (() => {
                              // Effective available for an original draft
                              // row = current_stock + the row's own original
                              // reservation (which will be restored at save).
                              // For new lines, it's just current_stock.
                              const baseStock = Number(opt?.current_stock ?? r.item_stock ?? 0);
                              const effective = isOriginalRow
                                ? baseStock + Number(r.original_qty || 0)
                                : baseStock;
                              return (
                                <div className="text-xs text-zinc-500 mt-1">
                                  Available: <span className="font-mono">{effective}</span>
                                  {" "}{opt?.unit || r.item_unit || ""}
                                  {isOriginalRow && Number(r.original_qty || 0) > 0 && (
                                    <span className="ml-1 text-zinc-400">
                                      (incl. {r.original_qty} reserved by this draft)
                                    </span>
                                  )}
                                </div>
                              );
                            })()
                          )}
                        </div>
                        <div className="col-span-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRow(idx)}
                            disabled={rows.length === 1 || rowLocked}
                            data-testid={`dispatch-row-remove-${idx}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-2 items-start">
                        <div className="col-span-3">
                          <Label className="text-xs uppercase tracking-wider text-zinc-500">Qty</Label>
                          <Input
                            type="number"
                            step="any"
                            placeholder="Qty"
                            value={isFreight ? "1" : r.quantity}
                            onChange={(e) => setRow(idx, { quantity: e.target.value })}
                            onWheel={(e) => e.currentTarget.blur()}
                            disabled={rowLocked || isFreight}
                            data-testid={`dispatch-row-qty-${idx}`}
                            className={`no-spinner ${stockWarn ? "border-rose-400" : ""}`}
                            readOnly={isFreight || !!r.locked_from_lot}
                          />
                          {stockWarn && (
                            <div className="text-xs text-rose-600 mt-1">Exceeds stock</div>
                          )}
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs uppercase tracking-wider text-zinc-500">Rate (excl. tax)</Label>
                          <Input
                            type="number"
                            step="any"
                            placeholder="Rate"
                            value={r.rate}
                            onChange={(e) => setRow(idx, { rate: e.target.value })}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="no-spinner"
                            data-testid={`dispatch-row-rate-${idx}`}
                            /* Aug 2026 — BUYER_SKU dispatches take rate
                               strictly from customer-specific price_master
                               (resolved server-side on submit). Freight
                               and RM lines keep the editable input; freight
                               is a manual charge, RM uses its own
                               vendor-price path. */
                            readOnly={!isFreight && form.item_type === "BUYER_SKU"}
                            disabled={!isFreight && form.item_type === "BUYER_SKU"}
                          />
                          <RateSourceBadge source={r.rate_source} />
                          {/* Oct 2026 (AC1) — blocking inline notice when
                              a BUYER_SKU row has no Price Master. The badge
                              above still renders but it's the small
                              informational one; this red strip tells
                              Finance exactly what to do and matches the
                              submit guard above. */}
                          {!isFreight && form.item_type === "BUYER_SKU" && !r.rate && r.item_id && (
                            <div
                              className="mt-1 text-[11px] px-2 py-1 rounded border border-rose-300 bg-rose-50 text-rose-800 font-medium"
                              data-testid={`dispatch-row-no-price-${idx}`}
                            >
                              {r.buyer_link_missing
                                ? "Buyer not linked to Playfield — Price Master cannot resolve. Link the buyer first."
                                : "No Price Master entry for this customer × SKU. Add one under Price Master before saving."}
                            </div>
                          )}
                          {!isFreight && (
                          <button
                            type="button"
                            className={`mt-1 w-full text-[11px] py-1 rounded border font-medium transition ${
                              r.item_id && form.customer_id && Number(r.rate) > 0 && r.tax_id
                                ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                : "border-zinc-200 bg-zinc-50 text-zinc-400 cursor-not-allowed"
                            }`}
                            disabled={!(r.item_id && form.customer_id && Number(r.rate) > 0 && r.tax_id)}
                            onClick={() => handleUpdatePrice(idx)}
                            data-testid={`dispatch-row-update-price-${idx}`}
                            title={
                              !form.customer_id ? "Pick a customer first"
                              : !r.item_id ? "Pick an item first"
                              : !(Number(r.rate) > 0) ? "Enter a rate > 0"
                              : !r.tax_id ? "Pick a tax"
                              : "Save this rate for future dispatches to this customer"
                            }
                          >
                            Update Price
                          </button>
                          )}
                        </div>
                        <div className="col-span-4">
                          <Label className="text-xs uppercase tracking-wider text-zinc-500">Tax *</Label>
                          <Select
                            value={r.tax_id}
                            onValueChange={(v) => setRow(idx, { tax_id: v, gst_resolve_gap: null })}
                          >
                            <SelectTrigger data-testid={`dispatch-row-tax-${idx}`}>
                              <SelectValue placeholder={zohoTaxes.length === 0 ? "Loading taxes..." : "Pick tax"} />
                            </SelectTrigger>
                            <SelectContent>
                              {zohoTaxes.map((t) => (
                                <SelectItem key={t.tax_id} value={t.tax_id}>
                                  {t.tax_name} ({t.tax_percentage}%)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {r.gst_resolve_gap && (
                            <div
                              className="mt-1.5 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
                              title={r.gst_resolve_gap.reason}
                              data-testid={`dispatch-row-tax-gap-${idx}`}
                            >
                              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-600"/>
                              <span className="leading-snug">
                                Tax not resolved
                                {r.gst_resolve_gap.rate != null && (
                                  <> · {r.gst_resolve_gap.rate}% {r.gst_resolve_gap.kind || ""}</>
                                )}
                                {" — "}
                                <span className="text-amber-800">{r.gst_resolve_gap.reason}</span>
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Per-line Amount — matches the RM Inward layout
                            (qty × rate, plus the line's share of the bill-
                            level discount + computed tax shown below). */}
                        <div className="col-span-2 text-right">
                          <Label className="text-xs uppercase tracking-wider text-zinc-500">Amount</Label>
                          <div className="font-mono font-semibold text-base mt-1.5" data-testid={`dispatch-row-amount-${idx}`}>
                            {fmtINR(linesWithTax[idx]?.gross || 0)}
                          </div>
                          {linesWithTax[idx]?.tax > 0 && (
                            <div className="text-[11px] text-zinc-500 font-mono">
                              +{fmtINR(linesWithTax[idx].tax)} tax
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-center text-xs text-zinc-500 mt-2">
                <span>Total Qty: <b className="text-zinc-700 font-mono">{totalRowsSumQty}</b></span>
                <button
                  type="button"
                  onClick={addRow}
                  disabled={!form.source_branch || linesLocked || !!selectedLot}
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 disabled:text-zinc-300 disabled:cursor-not-allowed"
                  data-testid="dispatch-add-another-line"
                >
                  <Plus className="w-3.5 h-3.5" /> {selectedLot ? "Lines locked by lot" : "Add another line"}
                </button>
              </div>
            </div>

            {/* Notes + Totals panel (RM-Inward-style) ------------------------
                Left column: Notes + Reference / POS metadata.
                Right column: Sub Total → Discount → Tax → Grand Total. */}
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <Label>Reference Number</Label>
                  <Input
                    value={form.reference_number}
                    onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                    placeholder="Defaults to dispatch code"
                    data-testid="dispatch-reference"
                  />
                </div>
                <div>
                  <Label>Place of Supply (state)</Label>
                  <Input
                    value={form.place_of_supply}
                    onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })}
                    placeholder="e.g. MH, KA — required if customer has no saved POS"
                    data-testid="dispatch-pos"
                  />
                </div>
                <div>
                  <Label>Notes / Remarks</Label>
                  <Textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Optional notes for the invoice"
                    data-testid="dispatch-notes"
                  />
                </div>
              </div>

              {/* Totals panel — matches the RM Inward summary block */}
              <div className="border rounded-lg bg-zinc-50/40 p-4 self-start" data-testid="dispatch-totals">
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-3 items-center text-sm">
                  {/* Sub Total */}
                  <div className="text-zinc-600 font-medium">Sub Total</div>
                  <div></div>
                  <div className="font-mono font-semibold text-right" data-testid="dispatch-subtotal">
                    {fmtINR(subTotal)}
                  </div>

                  {/* Discount */}
                  <div className="text-zinc-600 font-medium">Discount</div>
                  <div className="flex gap-1.5">
                    <Select
                      value={form.discount_type}
                      onValueChange={(v) => setForm({ ...form, discount_type: v })}
                    >
                      <SelectTrigger className="h-8 w-[72px] text-xs" data-testid="dispatch-discount-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">%</SelectItem>
                        <SelectItem value="amount">₹</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.discount}
                      onChange={(e) => setForm({ ...form, discount: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="0"
                      className="no-spinner h-8 w-[90px] text-right text-xs"
                      data-testid="dispatch-discount"
                    />
                  </div>
                  <div className="font-mono text-right text-rose-700" data-testid="dispatch-discount-amount">
                    −{fmtINR(discountAmount)}
                  </div>

                  {/* Tax Total */}
                  <div className="text-zinc-600 font-medium">Tax Total</div>
                  <div className="text-[11px] text-zinc-500 text-right">
                    {linesWithTax.some((l) => l.pct > 0) ? "applied per-line" : ""}
                  </div>
                  <div className="font-mono font-semibold text-right text-emerald-700" data-testid="dispatch-tax-total">
                    +{fmtINR(taxTotal)}
                  </div>

                  {/* Grand Total */}
                  <div className="col-span-3 border-t mt-1 pt-3 grid grid-cols-[1fr_auto] items-center">
                    <div className="font-semibold text-zinc-800">Grand Total</div>
                    <div className="font-mono text-lg font-bold text-orange-700" data-testid="dispatch-grand-total">
                      {fmtINR(grandTotal)}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500 mt-3 leading-snug">
                  Discount is applied <b>before tax</b> — GST is computed on the
                  post-discount taxable value of each line.
                </p>
              </div>
            </div>

            {/* Blockers banner */}
            {blockers && (
              <div className="border border-rose-300 bg-rose-50 rounded p-3 text-sm" data-testid="dispatch-blockers">
                <div className="font-semibold text-rose-700 flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4" />
                  {blockers.type === "stock" && "Insufficient stock for these items"}
                  {blockers.type === "master" && "Missing master fields needed by Zoho"}
                  {blockers.type === "price_master" && "Price not set in Price Master"}
                </div>
                <ul className="space-y-1 text-rose-900">
                  {blockers.items.map((i, idx) => (
                    <li key={idx} className="font-mono text-xs">
                      {blockers.type === "stock"
                        ? (i.name && i.name !== i.item_id
                            ? `${i.name} (${i.item_id}): requested ${i.requested}, available ${i.available}, short ${i.shortage}`
                            : `${i.item_id}: requested ${i.requested}, available ${i.available}, short ${i.shortage}`)
                        : blockers.type === "master"
                        ? `${i.internal_id} (${i.found_kind}): ${i.reason}`
                        : `${i.item_id}: no Price Master entry`}
                    </li>
                  ))}
                </ul>
                {blockers.type === "price_master" && (
                  <div className="text-xs text-rose-700 mt-2">
                    Add a price entry for these SKU(s) in <b>Price Master</b> and try again.
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreate(false);
                setEditingId(null);
                setEditingStatus(null);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            {/* Feb 2026 — every save through the edit dialog is now
                local-only. DRAFT-with-Zoho edits flip status to
                DRAFT_UNSYNCED; the operator syncs explicitly via the
                detail-view "Sync to Zoho" action (mirrors the
                PLAYFIELD_DRAFT push-to-Zoho pattern). Create + edit
                of any editable status share the same footer button. */}
            <Button
              onClick={() => submitCreate("playfield_draft")}
              disabled={submitting}
              data-testid="dispatch-save-draft"
              title="Save changes locally — sync to Zoho separately from the detail view"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? "Save Draft" : "Save as Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dispatch-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="font-mono">{selected?.dispatch_code}</span>
              {selected?.status && (
                <Badge className={`${STATUS_COLORS[selected.status] || ""} border`}>
                  {STATUS_LABELS[selected.status] || selected.status}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selected?.customer_name} · {selected?.source_branch} ·{" "}
              {selected?.item_type === "RM" ? "Raw Material" : "Buyer SKU"}
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4 text-sm">
              {/* PLAYFIELD_DRAFT — quick actions row */}
              {selected.status === "PLAYFIELD_DRAFT" && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-700">
                    Saved in Playfield only. Stock is locked to this dispatch but
                    no invoice exists in Zoho yet.
                  </div>
                  <div className="flex gap-2">
                    <PermButton
                      entity="Dispatch" action="DELETE"
                      variant="outline"
                      size="sm"
                      className="border-rose-200 text-rose-700 hover:bg-rose-50"
                      onClick={() => handleDelete(selected)}
                      data-testid="dispatch-delete-btn"
                    >
                      Delete (release stock)
                    </PermButton>
                    <PermButton
                      entity="Dispatch" action="CREATE"
                      size="sm"
                      onClick={() => handlePushToZoho(selected)}
                      data-testid="dispatch-push-to-zoho-btn"
                    >
                      Push to Zoho
                    </PermButton>
                  </div>
                </div>
              )}

              {/* PUSH_FAILED — Feb 2026 (AC1). A Zoho push was
                  attempted and rejected — stock is deducted (matches
                  PLAYFIELD_DRAFT) but no live Zoho invoice exists (or
                  a dedup adoption is still needed). Retry uses the
                  same `handlePushToZoho` handler as PLAYFIELD_DRAFT,
                  which already covers PUSH_FAILED at its entry check.
                  No new handler, no second entry point. */}
              {selected.status === "PUSH_FAILED" && (
                <div className="bg-rose-50 border border-rose-300 rounded-md p-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-rose-900 flex-1 min-w-0">
                    <div>
                      <b>Push to Zoho failed.</b> Stock is locked to this
                      dispatch but Zoho never accepted the invoice. Fix the
                      error and retry — inventory stays deducted regardless.
                    </div>
                    {selected.zoho_push_error && (
                      <div className="mt-1 font-mono text-[11px] break-words" data-testid="dispatch-push-failed-error">
                        Last error: {selected.zoho_push_error}
                      </div>
                    )}
                    {selected.zoho_push_retry_count > 0 && (
                      <div className="mt-1 text-rose-700 text-[11px]" data-testid="dispatch-push-failed-retries">
                        Retries so far: {selected.zoho_push_retry_count}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <PermButton
                      entity="Dispatch" action="DELETE"
                      variant="outline"
                      size="sm"
                      className="border-rose-300 text-rose-800 hover:bg-rose-100"
                      onClick={() => handleDelete(selected)}
                      data-testid="dispatch-delete-push-failed-btn"
                    >
                      Delete (release stock)
                    </PermButton>
                    <PermButton
                      entity="Dispatch" action="CREATE"
                      size="sm"
                      onClick={() => handlePushToZoho(selected)}
                      data-testid="dispatch-push-to-zoho-btn-push-failed"
                    >
                      Push to Zoho
                    </PermButton>
                  </div>
                </div>
              )}

              {/* REJECTED — Feb 2026. Zoho voided the invoice; the
                  reject lifecycle already reversed stock and unlinked
                  the lot. The Delete action here is a pure soft-delete
                  stamp so operators can hide the row from the default
                  list once audit is done. No push-to-zoho — this
                  invoice's Zoho side is dead. */}
              {selected.status === "REJECTED" && (
                <div className="bg-rose-50 border border-rose-200 rounded-md p-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-rose-900">
                    Zoho voided this invoice. Stock was already released by
                    the reject lifecycle. Deleting only hides it from the
                    default list — the record is preserved for audit.
                  </div>
                  <div className="flex gap-2">
                    <PermButton
                      entity="Dispatch" action="DELETE"
                      variant="outline"
                      size="sm"
                      className="border-rose-300 text-rose-800 hover:bg-rose-100"
                      onClick={() => handleDelete(selected)}
                      data-testid="dispatch-delete-rejected-btn"
                    >
                      Delete (hide from list)
                    </PermButton>
                  </div>
                </div>
              )}

              {/* DRAFT_UNSYNCED — edit landed locally, needs an
                  explicit Zoho sync. Same click-pattern + rollback-
                  free semantics as the PLAYFIELD_DRAFT push button
                  above (see handlePushToZoho — same handler, endpoint
                  branches on status). */}
              {selected.status === "DRAFT_UNSYNCED" && (
                <div className="bg-orange-50 border border-orange-300 rounded-md p-3 flex items-center justify-between gap-3">
                  <div className="text-xs text-orange-900">
                    <b>Local edit saved, not yet in Zoho.</b> The Zoho
                    invoice still holds the previous state. Sync now
                    to push the saved changes; on Zoho rejection the
                    dispatch stays in this state for you to retry.
                    {selected.zoho_sync_error && (
                      <div className="mt-1 text-rose-800">
                        Last sync error: <span className="font-mono">{selected.zoho_sync_error}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <PermButton
                      entity="Dispatch" action="CREATE"
                      size="sm"
                      onClick={() => handlePushToZoho(selected)}
                      data-testid="dispatch-sync-to-zoho-btn"
                    >
                      Sync to Zoho
                    </PermButton>
                  </div>
                </div>
              )}

              {/* DRAFT (already in Zoho) — persistent warning banner per
                  user spec: Zoho-side quantity / line-item edits will NOT
                  sync back to Playfield. */}
              {selected.status === "DRAFT" && selected.zoho_invoice_id && (
                <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-xs text-amber-900">
                  <b>Invoice now lives in Zoho Books.</b> Quantity / line-item
                  changes you make directly in Zoho will NOT sync back to
                  Playfield. Edit here to keep both in sync.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500">Invoice Date</div>
                  <div className="font-mono">{selected.date}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500">Zoho Invoice</div>
                  <div className="font-mono">
                    {selected.zoho_invoice_number || "-"}
                    {selected.zoho_invoice_status && (
                      <span className="ml-2 text-zinc-500">({selected.zoho_invoice_status})</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500">Reference</div>
                  <div className="font-mono">{selected.reference_number || "-"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500">Total</div>
                  <div className="font-mono font-bold">
                    ₹{Number(selected.total_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {selected.notes && (
                  <div className="col-span-2">
                    <div className="text-xs uppercase tracking-wider text-zinc-500">Notes</div>
                    <div>{selected.notes}</div>
                  </div>
                )}
                {selected.stock_reversed && (
                  <div className="col-span-2 text-rose-700">
                    Stock reversed on {selected.rejected_at} — reason: {selected.rejected_reason}
                  </div>
                )}
              </div>

              <Separator />

              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Line Items</div>
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b text-xs uppercase">
                    <tr>
                      <th className="px-2 py-2 text-left">Item</th>
                      <th className="px-2 py-2 text-left">Name</th>
                      <th className="px-2 py-2 text-right">Qty</th>
                      <th className="px-2 py-2 text-right">Rate</th>
                      <th className="px-2 py-2 text-left">Tax</th>
                      <th className="px-2 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.line_items || []).map((li, idx) => {
                      const tax = zohoTaxes.find((t) => t.tax_id === li.tax_id);
                      return (
                        <tr key={idx} className="border-b">
                          <td className="px-2 py-2 font-mono text-xs">{li.item_id}</td>
                          <td className="px-2 py-2">{li.name}</td>
                          <td className="px-2 py-2 text-right">{li.quantity} {li.unit || ""}</td>
                          <td className="px-2 py-2 text-right font-mono">₹{Number(li.rate).toFixed(2)}</td>
                          <td className="px-2 py-2 text-xs">
                            {tax ? `${tax.tax_name} (${tax.tax_percentage}%)` : li.tax_id || "-"}
                            {li.gst_resolve_gap && (
                              <div
                                className="mt-1 inline-flex items-start gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900"
                                title={li.gst_resolve_gap.reason}
                                data-testid={`dispatch-detail-tax-gap-${idx}`}
                              >
                                <AlertTriangle className="w-2.5 h-2.5 mt-0.5 flex-shrink-0 text-amber-600"/>
                                <span>Tax not resolved</span>
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right font-mono">₹{Number(li.amount).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dispatch;
